import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import useLeads from '../../hooks/useLeads';
import LeadForm from '../ui/LeadForm';

// SVG icon components for product categories
const WaterDropIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
  </svg>
);

const BuildingIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" /><path d="M16 6h.01" />
    <path d="M8 10h.01" /><path d="M16 10h.01" />
    <path d="M8 14h.01" /><path d="M16 14h.01" />
  </svg>
);

const FlameIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
);

const WrenchIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

const categories = [
  {
    title: 'Трубы полиэтиленовые для водоснабжения',
    description: 'Диаметр 20-1200 мм',
    icon: WaterDropIcon,
    image: '/images/pipes-delivery-formats.png',
    link: '/catalog/water',
    features: ['Питьевая вода', 'Техническая вода', 'Напорные системы'],
    color: 'from-blue-500 to-blue-600'
  },
  {
    title: 'Трубы полиэтиленовые для канализации',
    description: 'Диаметр 20-1200 мм',
    icon: BuildingIcon,
    image: '/images/pipes-warehouse.jpg',
    link: '/catalog/sewer',
    features: ['Безнапорные системы', 'Ливневая канализация', 'Гладкостенные'],
    color: 'from-gray-500 to-gray-600'
  },
  {
    title: 'Трубы полиэтиленовые для газоснабжения',
    description: 'Диаметр 32-630 мм',
    icon: FlameIcon,
    image: '/images/gas-pipes-single-layer.png',
    link: '/catalog/gas',
    features: ['Природный газ', 'Сжиженный газ', 'Промышленное газоснабжение'],
    color: 'from-yellow-500 to-orange-500'
  },
  {
    title: 'Трубы с соэкструзионными слоями',
    description: 'Многослойные трубы',
    icon: WrenchIcon,
    image: '/images/gas-pipes-multi-layer.png',
    link: '/catalog/coextruded',
    features: ['Повышенная прочность', 'Барьерные свойства', 'Специальные применения'],
    color: 'from-green-500 to-green-600'
  },
];

const ProductCategories = () => {
  const {
    isLeadFormOpen,
    leadFormData,
    closeLeadForm,
    requestConsultation
  } = useLeads();

  return (
    <section className="py-20 bg-white dark:bg-secondary-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">

          <h2 className="text-3xl lg:text-4xl font-bold text-secondary-900 dark:text-white mb-6">
            Наша <span className="text-primary-600">продукция</span>
          </h2>
          <p className="text-xl text-secondary-600 dark:text-secondary-300 max-w-3xl mx-auto">
            Широкий ассортимент полиэтиленовых труб и комплектующих для любых инженерных задач
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
          {categories.map((category, index) => (
            <Link
              key={index}
              to={category.link}
              className="group relative bg-white dark:bg-secondary-800 rounded-2xl shadow-custom hover:shadow-card overflow-hidden transition-all duration-300 transform hover:-translate-y-2 border border-secondary-100 dark:border-secondary-700"
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
              
              {/* Product Image */}
              <div className="relative h-48 overflow-hidden rounded-t-2xl">
                <img 
                  src={category.image} 
                  alt={category.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <category.icon className="h-6 w-6 text-primary-600" />
                </div>
              </div>

              {/* Content */}
              <div className="relative p-6">{/* Removed icon div as it's now in the image overlay */}
                
                {/* Title and Description */}
                <h3 className="text-lg sm:text-xl font-bold text-secondary-900 dark:text-white mb-3 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors break-words leading-tight">
                  {category.title}
                </h3>
                <p className="text-secondary-600 dark:text-secondary-300 mb-4 font-medium text-sm sm:text-base">{category.description}</p>
                
                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {category.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-secondary-500 dark:text-secondary-400">
                      <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                
                {/* CTA */}
                <div className="flex items-center text-primary-600 dark:text-primary-400 font-semibold group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors">
                  Подробнее
                  <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
              
              {/* Hover Border Effect */}
              <div className="absolute inset-0 rounded-2xl ring-2 ring-transparent group-hover:ring-primary-200 transition-all duration-300"></div>
            </Link>
          ))}
        </div>
        
        {/* Call to Action */}
        <div className="mt-16 text-center">
          <p className="text-lg text-secondary-600 dark:text-secondary-300 mb-6">
            Не нашли нужную продукцию? Свяжитесь с нами для консультации
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pricelist"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors duration-200"
            >
              Посмотреть прайс
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
            <button 
              onClick={() => requestConsultation()}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 font-semibold rounded-xl hover:border-primary-600 dark:hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
            >
              Получить консультацию
            </button>
          </div>
        </div>
      </div>

      {/* Lead Form */}
      <LeadForm
        isOpen={isLeadFormOpen}
        onClose={closeLeadForm}
        leadType={leadFormData.leadType}
        productName={leadFormData.productName}
        productId={leadFormData.productId}
        calculatedPrice={leadFormData.calculatedPrice}
      />
    </section>
  );
};

export default ProductCategories;